import { TippyTooltipDirective } from '../tooltip/tippy-tooltip.directive';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  viewChild,
  effect,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ControlContainer,
} from '@angular/forms';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { ClickOutSideDirective } from '../../directives/clickOutSide.directive';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ChevronDownIconComponent } from '@kuiper/icons';

@Component({
  selector: 'lib-date-range',
  imports: [
    TippyTooltipDirective,
    ReactiveFormsModule,
    DatePickerComponent,
    ClickOutSideDirective,
    TranslocoPipe,
    ChevronDownIconComponent,
  ],
  templateUrl: './date-range.component.html',
  styleUrl: './date-range.component.scss',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DateRangeComponent implements OnInit, OnDestroy {
  private parentContainer = inject(ControlContainer, { optional: true });
  private cdr = inject(ChangeDetectorRef);
  fb = inject(FormBuilder);
  private transloco = inject(TranslocoService);

  startDatePicker = viewChild<DatePickerComponent>('startDatePicker');
  endDatePicker = viewChild<DatePickerComponent>('endDatePicker');

  startDateControlName = input.required<string>();
  endDateControlName = input.required<string>();
  startDateLabel = input<string>('Start Date');
  endDateLabel = input<string>('End Date');

  startDatePlaceholder = input<string>('date_range_placeholder');
  endDatePlaceholder = input<string>('date_range_placeholder');
  placeholder = input<string>('date_range_placeholder');

  startDateId = input<string>('start-date');
  endDateId = input<string>('end-date');
  startDateName = input<string>('startDate');
  endDateName = input<string>('endDate');

  datesApplied = output<{ startDate: string; endDate: string }>();

  dateRangeForm: FormGroup;
  private formValueSubscription?: Subscription;
  private startDateValue = signal<Date | null>(null);
  appliedStartDate = signal<string>('');
  appliedEndDate = signal<string>('');
  isDropdownOpen = signal<boolean>(false);
  private calendarOpenTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    // Check if we're inside a parent form group
    if (this.parentContainer?.control) {
      this.dateRangeForm = this.parentContainer.control as FormGroup;
    } else {
      // Create our own form group
      this.dateRangeForm = this.fb.group({});
    }

    // Effect to open calendars when dropdown opens
    effect(() => {
      if (this.isDropdownOpen()) {
        if (this.calendarOpenTimeout) {
          clearTimeout(this.calendarOpenTimeout);
        }
        this.calendarOpenTimeout = setTimeout(() => {
          this.openBothCalendars();
        }, 0);
      }
    });
  }

  ngOnInit() {
    this.initializeFormControls();
    this.setupFormSubscriptions();
    this.initializeAppliedDates();
  }

  private initializeFormControls(): void {
    if (!this.parentContainer?.control) {
      const formControls: Record<string, any> = {};
      formControls[this.startDateControlName()] = '';
      formControls[this.endDateControlName()] = { value: '', disabled: true };
      this.dateRangeForm = this.fb.group(formControls);
    } else {
      this.ensureControlsInParentForm();
    }
  }

  private ensureControlsInParentForm(): void {
    const startControl = this.dateRangeForm.get(this.startDateControlName());
    const endControl = this.dateRangeForm.get(this.endDateControlName());

    if (!startControl) {
      this.dateRangeForm.addControl(
        this.startDateControlName(),
        this.fb.control('')
      );
    }

    if (!endControl) {
      this.dateRangeForm.addControl(
        this.endDateControlName(),
        this.fb.control({ value: '', disabled: true })
      );
    } else {
      // Ensure end date control is disabled initially
      if (!endControl.disabled) {
        endControl.disable();
      }
    }
  }

  private setupFormSubscriptions(): void {
    const startControl = this.dateRangeForm.get(this.startDateControlName());
    const endControl = this.dateRangeForm.get(this.endDateControlName());

    if (startControl) {
      this.subscribeToStartDateChanges(startControl, endControl);
    }

    if (endControl) {
      this.subscribeToEndDateChanges(endControl, startControl);
    }
  }

  private subscribeToStartDateChanges(
    startControl: any,
    endControl: any
  ): void {
    this.formValueSubscription = startControl.valueChanges.subscribe(
      (value: string | null) => {
        this.handleStartDateChange(value);
        // Clear applied dates when both controls are cleared (including external resets)
        if (!value || value === '') {
          const endValue = endControl?.value || '';
          if (!endValue || endValue === '') {
            this.appliedStartDate.set('');
            this.appliedEndDate.set('');
          }
        }
      }
    );
  }

  private subscribeToEndDateChanges(endControl: any, startControl: any): void {
    const endDateSubscription = endControl.valueChanges.subscribe(
      (value: string | null) => {
        if (!value || value === '') {
          const startValue = startControl?.value || '';
          if (!startValue || startValue === '') {
            this.appliedStartDate.set('');
            this.appliedEndDate.set('');
          }
        }
      }
    );
    this.formValueSubscription?.add(endDateSubscription);
  }

  private initializeAppliedDates(): void {
    const startControl = this.dateRangeForm.get(this.startDateControlName());
    const endControl = this.dateRangeForm.get(this.endDateControlName());

    if (startControl?.value) {
      this.handleStartDateChange(startControl.value);
      this.appliedStartDate.set(startControl.value);
    }

    if (endControl?.value) {
      this.appliedEndDate.set(endControl.value);
    }
  }

  private openBothCalendars() {
    const startControl = this.dateRangeForm.get(this.startDateControlName());
    const endControl = this.dateRangeForm.get(this.endDateControlName());

    // Open start date calendar and set its selectedDate
    const startPicker = this.startDatePicker();
    if (startPicker) {
      const startValue = startControl?.value;
      if (startValue) {
        const parsedStartDate = this.parseDateValue(startValue);
        if (parsedStartDate) {
          startPicker.selectedDate = parsedStartDate;
        }
      }
      startPicker.showCalendar.set(true);
    }

    // Open end date calendar and set its selectedDate
    const endPicker = this.endDatePicker();
    if (endPicker) {
      const endValue = endControl?.value;
      if (endValue) {
        const parsedEndDate = this.parseDateValue(endValue);
        if (parsedEndDate) {
          endPicker.selectedDate = parsedEndDate;
        }
      }
      endPicker.showCalendar.set(true);
    }
  }

  ngOnDestroy() {
    this.formValueSubscription?.unsubscribe();
    if (this.calendarOpenTimeout) {
      clearTimeout(this.calendarOpenTimeout);
    }
  }

  toggleDropdown() {
    this.isDropdownOpen.update(value => !value);

    if (!this.isDropdownOpen()) {
      // Restore form to applied values when opening
      const startControl = this.dateRangeForm.get(this.startDateControlName());
      const endControl = this.dateRangeForm.get(this.endDateControlName());

      if (startControl) {
        startControl.setValue(this.appliedStartDate());
        if (this.appliedStartDate()) {
          const parsedDate = this.parseDateValue(this.appliedStartDate());
          if (parsedDate) {
            this.startDateValue.set(parsedDate);
          }
        }
      }

      if (endControl) {
        const endValue = this.appliedEndDate();
        if (endValue) {
          endControl.setValue(endValue);
          endControl.enable();
        } else {
          endControl.setValue('');
          if (!this.startDateValue()) {
            endControl.disable();
          }
        }
      }
    }
  }

  closeDropdown() {
    this.isDropdownOpen.set(false);
  }

  handleStartDateChange(value: string | Date | null) {
    if (value) {
      const date = this.parseDateValue(value);
      if (date) {
        this.startDateValue.set(date);
        const endControl = this.dateRangeForm.get(this.endDateControlName());
        if (endControl) {
          endControl.enable();
        }
        const endPicker = this.endDatePicker();
        if (this.isDropdownOpen() && endPicker) {
          // Sync end date picker's selectedDate with current value
          const endValue = endControl?.value;
          if (endValue) {
            const parsedEndDate = this.parseDateValue(endValue);
            if (parsedEndDate) {
              endPicker.selectedDate = parsedEndDate;
            }
          }
          endPicker.showCalendar.set(true);
        }
      }
    } else {
      this.startDateValue.set(null);
      const endControl = this.dateRangeForm.get(this.endDateControlName());
      if (endControl) {
        endControl.disable();
        endControl.setValue('');
      }
    }
  }

  getMinDateForEndDate(): Date | undefined {
    const startDate = this.startDateValue();
    if (startDate) {
      return startDate;
    }
    return undefined;
  }

  isEndDateDisabled(): boolean {
    return !this.startDateValue();
  }

  getDisplayText(): string {
    const startValue = this.appliedStartDate();
    const endValue = this.appliedEndDate();

    if (!startValue && !endValue) {
      return this.transloco.translate(this.placeholder());
    }

    if (startValue && endValue) {
      const startDate = this.parseDateValue(startValue);
      const endDate = this.parseDateValue(endValue);
      if (startDate && endDate) {
        return `${moment(startDate).format('DD-MMM-yyyy')} : ${moment(endDate).format('DD-MMM-yyyy')}`;
      }
    }

    return this.transloco.translate(this.placeholder());
  }

  canApply(): boolean {
    // Check if both dates are selected in the form
    const startControl = this.dateRangeForm.get(this.startDateControlName());
    const endControl = this.dateRangeForm.get(this.endDateControlName());

    const startValue = startControl?.value || '';
    const endValue = endControl?.value || '';

    return !!(startValue && endValue);
  }

  private parseDateValue(value: string | Date): Date | null {
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const cleanValue = value.split(' - (')[0].trim();
      const parsedDate = moment(cleanValue, 'DD-MMM-yyyy', true).toDate();
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    return null;
  }

  onApply() {
    // Save current form values as applied values
    const startControl = this.dateRangeForm.get(this.startDateControlName());
    const endControl = this.dateRangeForm.get(this.endDateControlName());

    const startValue = startControl?.value || '';
    const endValue = endControl?.value || '';

    this.appliedStartDate.set(startValue);
    this.appliedEndDate.set(endValue);

    // Emit event to notify parent that dates have been applied
    if (startValue && endValue) {
      this.datesApplied.emit({ startDate: startValue, endDate: endValue });
    }

    this.closeDropdown();
  }

  onCancel() {
    this.closeDropdown();
  }
}
